import { useEffect, useState, useCallback, useRef } from "react";
import SendMessage from "../components/SendMessage";
import ViewMessages from "../components/ViewMessages";
import ViewActions from "../components/ViewActions";
import InfoCard from "../components/InfoCard";
import DashboardHeader from "../components/DashboardHeader";
import PlayerActions from "../components/PlayerActions";
import ActionModal from "../components/ActionModal";
import ErrorToast from "../components/ErrorToast";
import { useTimer } from "../contexts/TimerContext";
import { PlayerRole, type Role, MAX_ALLOWED_SPACESHIP_DAMAGE, AgencyAction } from "../shared/constants";
import { API_BASE_URL, authenticatedPlayerFetch } from "../utils/api";

interface LandingSiteInvestment {
  player_id: string;
  player_name: string;
  team_name: string;
  count: number;
}

interface LandingSiteStatusContentProps {
  playerId: string;
  investments: LandingSiteInvestment[];
  isLoading: boolean;
  onLoadInvestments: (investments: LandingSiteInvestment[]) => void;
  onSetLoading: (loading: boolean) => void;
  onUpdateTotal?: (total: number) => void;
}

function LandingSiteStatusContent({ playerId, investments, isLoading, onLoadInvestments, onSetLoading }: LandingSiteStatusContentProps) {
  // Use data passed from parent - no need to fetch again since parent already has it
  const totalInvestments = investments.reduce((sum, inv) => sum + inv.count, 0);

  return (
    <div style={{ padding: "1rem 0" }}>
      {isLoading ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "#6c757d" }}>
          Loading landing site investments...
        </div>
      ) : investments.length === 0 ? (
        <div style={{ padding: "2rem", textAlign: "center", color: "#6c757d" }}>
          <p>No investments in your landing site yet.</p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: "1.5rem" }}>
            <h3 style={{ margin: "0 0 0.5rem 0", color: "#212529", fontSize: "1.1rem" }}>
              Total Landing Site Points: {totalInvestments}
            </h3>
            <p style={{ margin: 0, color: "#6c757d", fontSize: "0.9rem" }}>
              Number of players who have invested: {investments.length}
            </p>
          </div>
          <div style={{ borderTop: "1px solid #dee2e6", paddingTop: "1rem" }}>
            <h4 style={{ margin: "0 0 1rem 0", color: "#212529", fontSize: "1rem" }}>
              Investment Breakdown:
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {investments.map((investment) => (
                <div
                  key={investment.player_id}
                  style={{
                    padding: "0.75rem 1rem",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "4px",
                    border: "1px solid #dee2e6",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center"
                  }}
                >
                  <div>
                    <div style={{ fontWeight: "600", color: "#212529", marginBottom: "0.25rem" }}>
                      {investment.team_name}
                    </div>
                  </div>
                  <div style={{
                    fontSize: "1.25rem",
                    fontWeight: "bold",
                    color: "#6f42c1",
                    minWidth: "3rem",
                    textAlign: "right"
                  }}>
                    {investment.count} {investment.count === 1 ? "point" : "points"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

interface DashboardProps {
  playerId: string;
  onLogout: () => void;
}

export default function PlayerDashboard({ playerId, onLogout }: DashboardProps) {
  const { timerSeconds, isPaused } = useTimer();
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [currentTurn, setCurrentTurn] = useState<number | null>(null);
  const [maxTurns, setMaxTurns] = useState<number | null>(null);
  const [gameStatus, setGameStatus] = useState<string | null>(null);
  const [timerEnabled, setTimerEnabled] = useState<boolean>(true);
  const [limitActions, setLimitActions] = useState<boolean>(true);
  const [isLoadingTurn, setIsLoadingTurn] = useState<boolean>(true);
  const [playerTeamName, setPlayerTeamName] = useState<string>("");
  const [playerTeamId, setPlayerTeamId] = useState<string>("");
  const [playerGameName, setPlayerGameName] = useState<string>("");
  const [playerRole, setPlayerRole] = useState<Role>(PlayerRole.AGENCY);
  const [playerLoginCode, setPlayerLoginCode] = useState<string>("");
  const [playerCommLevel, setPlayerCommLevel] = useState<number | null>(null);
  const [playerScanLevel, setPlayerScanLevel] = useState<number | null>(null);
  const [playerDamage, setPlayerDamage] = useState<number | null>(null);
  const [playerShieldsOn, setPlayerShieldsOn] = useState<boolean>(false);
  const [, setPlayerPersonalScore] = useState<number>(0);
  const [actionCount, setActionCount] = useState<number | null>(null);
  const [charsSentThisTurn, setCharsSentThisTurn] = useState<number>(0);
  const [activeView, setActiveView] = useState<"sendMessage" | "viewMessages" | "viewActions" | null>(null);
  const [playerCurrentLocation, setPlayerCurrentLocation] = useState<string | null>(null);
  const [spaceshipLocation, setSpaceshipLocation] = useState<string | null>(null); // For agency players
  const [isLandingSiteModalOpen, setIsLandingSiteModalOpen] = useState<boolean>(false);
  const [landingSiteInvestments, setLandingSiteInvestments] = useState<Array<{player_id: string; player_name: string; team_name: string; count: number}>>([]);
  const [isLoadingInvestments, setIsLoadingInvestments] = useState<boolean>(false);
  const [totalLandingSiteInvestments, setTotalLandingSiteInvestments] = useState<number>(0);
  const [landingSiteMatrix, setLandingSiteMatrix] = useState<{
    teams: string[];
    investments: Record<string, Record<string, number>>; // investingTeam -> targetTeam -> count
    totals: Record<string, number>; // targetTeam -> total
  } | null>(null);
  const [isLoadingLandingSiteMatrix, setIsLoadingLandingSiteMatrix] = useState<boolean>(false);
  const [hasUnreadMessages, setHasUnreadMessages] = useState<boolean>(false);
  const [hasUnreadActions, setHasUnreadActions] = useState<boolean>(false);
  const [showErrorToast, setShowErrorToast] = useState<boolean>(false);
  const [replyTargetPlayerId, setReplyTargetPlayerId] = useState<string | null>(null);

  // Use ref to track current activeView in WebSocket handlers
  const activeViewRef = useRef(activeView);
  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  // Use ref to track current playerRole, playerTeamId, and playerId in WebSocket handlers
  const playerRoleRef = useRef(playerRole);
  const playerTeamIdRef = useRef(playerTeamId);
  const playerIdRef = useRef(playerId);
  useEffect(() => {
    playerRoleRef.current = playerRole;
    playerTeamIdRef.current = playerTeamId;
    playerIdRef.current = playerId;
  }, [playerRole, playerTeamId, playerId]);

  // Cache utilities for player dashboard data
  const PLAYER_DATA_CACHE_KEY = (playerId: string) => `lost-in-space-player-data-${playerId}`;
  const PLAYER_DATA_CACHE_TTL = 2 * 60 * 1000; // 2 minutes in milliseconds

  const getCachedPlayerData = useCallback((playerId: string): any | null => {
    try {
      const cached = localStorage.getItem(PLAYER_DATA_CACHE_KEY(playerId));
      if (!cached) return null;

      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is still valid
      if (now - timestamp > PLAYER_DATA_CACHE_TTL) {
        localStorage.removeItem(PLAYER_DATA_CACHE_KEY(playerId));
        return null;
      }

      return data;
    } catch (err) {
      console.error("Error reading player data cache:", err);
      localStorage.removeItem(PLAYER_DATA_CACHE_KEY(playerId));
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCachedPlayerData = useCallback((playerId: string, data: any) => {
    try {
      const cacheData = {
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(PLAYER_DATA_CACHE_KEY(playerId), JSON.stringify(cacheData));
    } catch (err) {
      console.error("Error writing player data cache:", err);
    }
  }, []);

  const applyDashboardData = useCallback((data: any) => {
    // Set player data
    const player = data.player;
    setPlayerTeamName(player.team_name || "");
    setPlayerTeamId(player.team_id || "");
    setPlayerGameName(player.game_name || "");
    setPlayerRole((player.role as Role) || PlayerRole.AGENCY);
    setPlayerLoginCode(player.login_code || "");
    setPlayerCommLevel(typeof player.comm_lvl === "number" ? player.comm_lvl : null);
    setPlayerScanLevel(typeof player.scan_lvl === "number" ? player.scan_lvl : null);
    setPlayerDamage(typeof player.ship_dmg === "number" ? player.ship_dmg : null);
    setPlayerShieldsOn(Boolean(player.shields_on));
    setPlayerCurrentLocation(player.current_location || null);
    setPlayerPersonalScore(typeof player.personal_score === "number" ? player.personal_score : 0);
    
    // Set game data
    const game = data.game;
    setCurrentTurn(game.current_turn);
    setMaxTurns(game.max_turns);
    setGameStatus(game.game_status);
    setTimerEnabled(game.timer_enabled ?? true);
    setLimitActions(game.limit_actions ?? true);
    setIsLoadingTurn(false);
    
    // Set action count
    setActionCount(data.action_count ?? 0);
    
    // Set character count
    setCharsSentThisTurn(data.chars_sent_this_turn ?? 0);
    
    // Set landing site investments (only for agency players)
    if (data.landing_site_investments && Array.isArray(data.landing_site_investments)) {
      const total = data.landing_site_investments.reduce((sum: number, inv: any) => sum + (inv.count || 0), 0);
      setTotalLandingSiteInvestments(total);
      setLandingSiteInvestments(data.landing_site_investments);
    } else {
      setTotalLandingSiteInvestments(0);
      setLandingSiteInvestments([]);
    }
  }, []);

  // Fetch all dashboard data in a single API call (with caching)
  const fetchDashboardData = useCallback((forceRefresh: boolean = false) => {
    if (!playerId) return;
    
    // Check cache first unless force refresh is requested
    if (!forceRefresh) {
      const cachedData = getCachedPlayerData(playerId);
      if (cachedData) {
        console.log("Using cached player dashboard data");
        applyDashboardData(cachedData);
        return;
      }
    }
    
    // Fetch from API
    setIsLoadingTurn(true);
    fetch(`${API_BASE_URL}/players/dashboard-data?player_id=${encodeURIComponent(playerId)}`)
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to fetch dashboard data");
        }
        return res.json();
      })
      .then((data) => {
        // Update cache
        setCachedPlayerData(playerId, data);
        
        // Apply data to state
        applyDashboardData(data);
      })
      .catch((err) => {
        console.error("Error fetching dashboard data:", err);
        setIsLoadingTurn(false);
        setActionCount(0);
        setTotalLandingSiteInvestments(0);
        setLandingSiteInvestments([]);
      });
  }, [playerId, getCachedPlayerData, setCachedPlayerData, applyDashboardData]);

  // Fetch dashboard data on mount
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Fetch spaceship location for agency players
  useEffect(() => {
    if (playerRole === PlayerRole.AGENCY && playerTeamId) {
      // Fetch team players to get spaceship location
      fetch(`${API_BASE_URL}/players/by-team-id?team_id=${encodeURIComponent(playerTeamId)}`)
        .then(res => {
          if (!res.ok) {
            if (res.status === 404) {
              return [];
            }
            throw new Error("Failed to fetch team players");
          }
          return res.json();
        })
        .then((players: any[]) => {
          const spaceship = players.find((p: any) => p.role === PlayerRole.SPACESHIP);
          if (spaceship) {
            setSpaceshipLocation(spaceship.current_location || null);
          }
        })
        .catch((err) => {
          console.error("Error fetching spaceship location:", err);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRole, playerTeamId]);

  // Invalidate cache and refetch action count and character count when turn changes (both reset each turn)
  useEffect(() => {
    if (!playerId || currentTurn === null) return;
    
    // Invalidate cache when turn changes (data is stale, especially action_count and chars_sent_this_turn)
    localStorage.removeItem(`lost-in-space-player-data-${playerId}`);
    
    // Refetch action count
    fetch(`${API_BASE_URL}/players/action-count?player_id=${encodeURIComponent(playerId)}`)
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to fetch action count");
        }
        return res.json();
      })
      .then((data) => {
        setActionCount(data.action_count ?? 0);
      })
      .catch((err) => {
        console.error("Error fetching action count:", err);
        setActionCount(0);
      });
    
    // Refetch character count for the new turn
    fetch(`${API_BASE_URL}/messages/chars-sent-this-turn?player_id=${encodeURIComponent(playerId)}`)
      .then(res => {
        if (!res.ok) {
          throw new Error("Failed to fetch character count");
        }
        return res.json();
      })
      .then((data) => {
        setCharsSentThisTurn(data.chars_sent ?? 0);
      })
      .catch((err) => {
        console.error("Error fetching character count:", err);
        setCharsSentThisTurn(0);
      });
  }, [playerId, currentTurn]);

  // Update total when investments array changes (e.g., from modal refresh)
  useEffect(() => {
    const total = landingSiteInvestments.reduce((sum, inv) => sum + inv.count, 0);
    setTotalLandingSiteInvestments(total);
  }, [landingSiteInvestments]);

  const applyPlayerPayload = useCallback((p: any) => {
    // Defensive check: only update if player_id matches current player
    // This prevents accidentally overwriting role from other players' updates
    if (p.player_id && p.player_id !== playerId) {
      console.warn("applyPlayerPayload: Attempted to apply payload for different player", {
        payloadPlayerId: p.player_id,
        currentPlayerId: playerId,
        payloadRole: p.role,
        currentRole: playerRole
      });
      return;
    }
    
    console.log("applyPlayerPayload: Applying payload", {
      playerId: p.player_id,
      role: p.role,
      currentPlayerId: playerId,
      currentRole: playerRole
    });
    
    setPlayerTeamName(p.team_name || "");
    setPlayerTeamId(p.team_id || "");
    setPlayerGameName(p.game_name || "");
    setPlayerRole((p.role as Role) || PlayerRole.AGENCY);
    setPlayerLoginCode(p.login_code || "");
    setPlayerCommLevel(typeof p.comm_lvl === "number" ? p.comm_lvl : null);
    setPlayerScanLevel(typeof p.scan_lvl === "number" ? p.scan_lvl : null);
    setPlayerCurrentLocation(p.current_location || null);
    setPlayerDamage(typeof p.ship_dmg === "number" ? p.ship_dmg : null);
    setPlayerShieldsOn(Boolean(p.shields_on));
    setPlayerPersonalScore(typeof p.personal_score === "number" ? p.personal_score : 0);
    if (typeof p.action_count === "number") {
      setActionCount(p.action_count);
    }
  }, [playerId, playerRole]);

  // Use refs for callbacks to avoid WebSocket reconnection when they change
  // Initialize with the functions directly to ensure they're available immediately
  const applyPlayerPayloadRef = useRef(applyPlayerPayload);
  const getCachedPlayerDataRef = useRef(getCachedPlayerData);
  const setCachedPlayerDataRef = useRef(setCachedPlayerData);
  const fetchLandingSiteMatrixRef = useRef<(() => Promise<void>) | null>(null);
  useEffect(() => {
    applyPlayerPayloadRef.current = applyPlayerPayload;
    getCachedPlayerDataRef.current = getCachedPlayerData;
    setCachedPlayerDataRef.current = setCachedPlayerData;
  }, [applyPlayerPayload, getCachedPlayerData, setCachedPlayerData]);

  // WebSocket connection management with reconnection
  useEffect(() => {
    if (!playerId) {
      console.warn("WebSocket: missing playerId, skipping connect");
      return;
    }

    let websocket: WebSocket | null = null;
    let reconnectAttempts = 0;
    let reconnectTimeoutId: NodeJS.Timeout | null = null;
    let heartbeatIntervalId: NodeJS.Timeout | null = null;
    let healthCheckIntervalId: NodeJS.Timeout | null = null;
    let lastPongReceived = Date.now();
    let isIntentionallyClosing = false;
    const MAX_RECONNECT_ATTEMPTS = 10;
    const HEARTBEAT_INTERVAL = 25000; // 25 seconds (slightly less than server's 30s)
    const HEALTH_CHECK_INTERVAL = 5000; // Check health every 5 seconds
    const PONG_TIMEOUT = 90000; // 90 seconds without pong = dead connection

    const connectWebSocket = () => {
      if (isIntentionallyClosing) return;

      // Convert HTTP API URL to WebSocket URL
      const apiUrl = new URL(API_BASE_URL);
      const wsProtocol = apiUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${apiUrl.host}/ws/${playerId}`;
      console.log("Attempting to connect WebSocket to:", wsUrl);
      
      websocket = new WebSocket(wsUrl);
      
      websocket.onopen = () => {
        console.log("WebSocket connected successfully");
        setWs(websocket);
        reconnectAttempts = 0; // Reset on successful connection
        lastPongReceived = Date.now();
        
        // Send an initial ping
        try {
          websocket!.send(JSON.stringify({ type: "ping" }));
        } catch (e) {
          console.warn("WebSocket initial ping failed:", e);
        }

        // Start periodic heartbeat
        heartbeatIntervalId = setInterval(() => {
          if (websocket && websocket.readyState === WebSocket.OPEN) {
            try {
              websocket.send(JSON.stringify({ type: "ping" }));
              console.debug("Sent heartbeat ping");
            } catch (e) {
              console.warn("Failed to send heartbeat ping:", e);
            }
          }
        }, HEARTBEAT_INTERVAL);

        // Start health check
        healthCheckIntervalId = setInterval(() => {
          const timeSinceLastPong = Date.now() - lastPongReceived;
          if (timeSinceLastPong > PONG_TIMEOUT) {
            console.warn(`No pong received for ${timeSinceLastPong}ms, connection may be dead. Reconnecting...`);
            if (websocket) {
              websocket.close();
            }
          }
        }, HEALTH_CHECK_INTERVAL);
      };
    
      websocket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("=== WebSocket message received ===", data);
          console.log("WebSocket refs status:", {
            applyPlayerPayloadRef: !!applyPlayerPayloadRef.current,
            getCachedPlayerDataRef: !!getCachedPlayerDataRef.current,
            setCachedPlayerDataRef: !!setCachedPlayerDataRef.current
          });
          
          // Handle pong responses
          if (data.type === "pong") {
            lastPongReceived = Date.now();
            console.debug("Received pong from server");
            return;
          }
          
          // Handle ping from server (respond with pong)
          if (data.type === "ping") {
            if (websocket && websocket.readyState === WebSocket.OPEN) {
              try {
                websocket.send(JSON.stringify({ type: "pong" }));
              } catch (e) {
                console.warn("Failed to send pong:", e);
              }
            }
            return;
          }
          
          if (data.type === "connected") {
            console.log("WebSocket connection confirmed:", data.message);
            return;
          }
          
          // Validate player_id and team_id for all other messages (except ping/pong/connected)
          // Both player_id and team_id must be present and match the current player's
          const currentPlayerTeamId = playerTeamIdRef.current;
          
          // Validate player_id - must be present and match
          if (data.player_id === undefined) {
            console.error("WebSocket message missing player_id:", {
              messageType: data.type,
              data: data
            });
            setShowErrorToast(true);
            return; // Reject the message - don't process it
          }
          if (data.player_id !== playerId) {
            console.error("WebSocket message player_id mismatch:", {
              received: data.player_id,
              expected: playerId,
              messageType: data.type
            });
            setShowErrorToast(true);
            return; // Reject the message - don't process it
          }
          
          // Validate team_id - must be present and match
          if (!currentPlayerTeamId) {
            console.warn("Player team_id not available for validation");
            // Continue processing if team_id is not available yet (might be during initial load)
          } else if (data.team_id === undefined) {
            console.error("WebSocket message missing team_id:", {
              messageType: data.type,
              data: data
            });
            setShowErrorToast(true);
            return; // Reject the message - don't process it
          } else if (data.team_id !== currentPlayerTeamId) {
            console.error("WebSocket message team_id mismatch:", {
              received: data.team_id,
              expected: currentPlayerTeamId,
              messageType: data.type
            });
            setShowErrorToast(true);
            return; // Reject the message - don't process it
          }
          
          if (data.type === "new_message") {
            console.log("New message notification received:", data);
            // Set notification badge for messages (only if not currently viewing messages)
            if (activeViewRef.current !== "viewMessages") {
              setHasUnreadMessages(true);
            }
            // Trigger a refresh of messages in ViewMessages component
            window.dispatchEvent(new CustomEvent('newMessage', { detail: { ...data.message, chars_sent_this_turn: data.chars_sent_this_turn } }));
            // Update character count from WebSocket if provided
            if (typeof data.chars_sent_this_turn === 'number') {
              setCharsSentThisTurn(data.chars_sent_this_turn);
            }
            if (data.player && data.player.player_id === playerIdRef.current) {
              applyPlayerPayloadRef.current(data.player);
              // Update cache with latest player data from WebSocket
              const cachedData = getCachedPlayerDataRef.current(playerId);
              if (cachedData) {
                const updatedCache = {
                  ...cachedData,
                  player: { ...cachedData.player, ...data.player },
                  chars_sent_this_turn: data.chars_sent_this_turn ?? cachedData.chars_sent_this_turn
                };
                setCachedPlayerDataRef.current(playerId, updatedCache);
              }
            }
          } else if (data.type === "game_update" && data.game) {
            // Update game information from server push
            console.log("Game update notification received:", data);
            console.log("Updating game state with:", {
              current_turn: data.game.current_turn,
              game_status: data.game.game_status,
              timer_enabled: data.game.timer_enabled
            });
            const game = data.game;
            setCurrentTurn(game.current_turn ?? null);
            setMaxTurns(game.max_turns ?? null);
            setGameStatus(game.game_status ?? null);
            setTimerEnabled(game.timer_enabled ?? true);
            setLimitActions(game.limit_actions ?? true);
            
            // Always update cache with latest game data from WebSocket
            const cachedData = getCachedPlayerDataRef.current(playerId);
            if (cachedData) {
              const updatedCache = {
                ...cachedData,
                game: { ...cachedData.game, ...game }
              };
              setCachedPlayerDataRef.current(playerId, updatedCache);
            }
            
            if (data.player && data.player.player_id === playerIdRef.current) {
              applyPlayerPayloadRef.current(data.player);
              // Update cache with latest player data from WebSocket
              const cachedData2 = getCachedPlayerDataRef.current(playerId);
              if (cachedData2) {
                const updatedCache = {
                  ...cachedData2,
                  player: { ...cachedData2.player, ...data.player },
                  game: { ...cachedData2.game, ...game }
                };
                setCachedPlayerDataRef.current(playerId, updatedCache);
              }
            } else if (Array.isArray(data.players)) {
              const currentPlayer = data.players.find((p: any) => p.player_id === playerId);
              if (currentPlayer) {
                applyPlayerPayloadRef.current(currentPlayer);
                // Update cache with latest player data from WebSocket
                const cachedData3 = getCachedPlayerDataRef.current(playerId);
                if (cachedData3) {
                  const updatedCache = {
                    ...cachedData3,
                    player: { ...cachedData3.player, ...currentPlayer },
                    game: game
                  };
                  setCachedPlayerDataRef.current(playerId, updatedCache);
                }
              }
            }
          } else if (data.type === "action_count_update") {
            // Update action count from server push
            console.log("Action count update received:", data);
            setActionCount(data.action_count ?? 0);
            if (data.player && data.player.player_id === playerIdRef.current) {
              applyPlayerPayloadRef.current(data.player);
              // Update cache with latest action count
              const cachedData = getCachedPlayerDataRef.current(playerId);
              if (cachedData) {
                const updatedCache = {
                  ...cachedData,
                  player: { ...cachedData.player, ...data.player },
                  action_count: data.action_count ?? cachedData.action_count
                };
                setCachedPlayerDataRef.current(playerId, updatedCache);
              }
            }
          } else if (data.type === "player_update" && data.player) {
            // If this is a spaceship location update and we're an agency player, update spaceship location
            // Do this BEFORE checking player_id to avoid any issues
            if (playerRoleRef.current === PlayerRole.AGENCY && data.player.role === PlayerRole.SPACESHIP && data.player.team_id === playerTeamIdRef.current) {
              setSpaceshipLocation(data.player.current_location || null);
            }
            
            // Only update local player info if this update is for the current logged-in player
            // Check both player_id and ensure we don't accidentally update role from other players
            if (data.player.player_id && data.player.player_id === playerIdRef.current) {
              console.log("Applying player_update for current player", {
                playerId: data.player.player_id,
                role: data.player.role,
                currentRole: playerRoleRef.current
              });
              applyPlayerPayloadRef.current(data.player);
              
              // Update cache with latest player data from WebSocket
              const cachedData = getCachedPlayerDataRef.current(playerId);
              if (cachedData) {
                const updatedCache = {
                  ...cachedData,
                  player: { ...cachedData.player, ...data.player }
                };
                setCachedPlayerDataRef.current(playerId, updatedCache);
              }
            } else {
              console.log("Ignoring player_update for different player", {
                payloadPlayerId: data.player.player_id,
                currentPlayerId: playerIdRef.current,
                payloadRole: data.player.role,
                currentRole: playerRoleRef.current
              });
            }
          } else if (data.type === "landing_site_update") {
            // Update landing site points from server push
            console.log("Landing site update received:", data);
            if (typeof data.total_points === "number") {
              setTotalLandingSiteInvestments(data.total_points);
            }
            if (Array.isArray(data.investments)) {
              setLandingSiteInvestments(data.investments);
            }
            if (data.player && data.player.player_id === playerIdRef.current) {
              applyPlayerPayloadRef.current(data.player);
              // Update cache with latest landing site data
              const cachedData = getCachedPlayerDataRef.current(playerId);
              if (cachedData) {
                const updatedCache = {
                  ...cachedData,
                  player: { ...cachedData.player, ...data.player },
                  landing_site_investments: data.investments ?? cachedData.landing_site_investments
                };
                setCachedPlayerDataRef.current(playerId, updatedCache);
              }
            }
            // Refresh landing site matrix for all agencies
            if (playerRoleRef.current === PlayerRole.AGENCY && fetchLandingSiteMatrixRef.current) {
              fetchLandingSiteMatrixRef.current().catch((err) => {
                console.error("Error refreshing landing site matrix:", err);
              });
            }
          } else if (data.type === "action_update") {
            console.log("Action update received:", data);
            // Set notification badge for actions (only if not currently viewing actions)
            if (activeViewRef.current !== "viewActions") {
              setHasUnreadActions(true);
            }
            if (typeof data.action_count === "number") {
              setActionCount(data.action_count);
            }
            if (data.player && data.player.player_id === playerIdRef.current) {
              applyPlayerPayloadRef.current(data.player);
              // Update cache with latest action count
              const cachedData = getCachedPlayerDataRef.current(playerId);
              if (cachedData) {
                const updatedCache = {
                  ...cachedData,
                  player: { ...cachedData.player, ...data.player },
                  action_count: data.action_count ?? cachedData.action_count
                };
                setCachedPlayerDataRef.current(playerId, updatedCache);
              }
            }
            // Notify ViewActions component with action data (no refetch needed)
            // Include scan level in the action data
            if (data.action && data.action.player_id === playerId) {
              window.dispatchEvent(new CustomEvent('actionCreated', { 
                detail: {
                  ...data.action,
                  scanning_agency_scan_lvl: data.action.scanning_agency_scan_lvl !== undefined ? data.action.scanning_agency_scan_lvl : null
                }
              }));
            }
            // Refresh landing site matrix if this is a Build Landing Site action
            if (data.action && data.action.action === AgencyAction.BUILD_LANDING_SITE) {
              if (playerRoleRef.current === PlayerRole.AGENCY && fetchLandingSiteMatrixRef.current) {
                fetchLandingSiteMatrixRef.current().catch((err) => {
                  console.error("Error refreshing landing site matrix:", err);
                });
              }
            }
          } else if (data.type === "timer_update") {
            // Timer update from admin - update timer via custom event with server time
            console.log("Timer update received:", data);
            window.dispatchEvent(new CustomEvent('timerUpdate', { 
              detail: { 
                timer_seconds: data.timer_seconds, 
                action: data.action,
                server_time: data.server_time,
                end_time: data.end_time,
                additional_seconds: data.additional_seconds
              } 
            }));
          }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };
    
      websocket.onerror = (error) => {
        console.error("WebSocket error:", error);
        console.error("WebSocket readyState:", websocket?.readyState);
      };
      
      websocket.onclose = (event) => {
        console.log("WebSocket disconnected", {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean
        });
        setWs(null);

        // Clear intervals
        if (heartbeatIntervalId) {
          clearInterval(heartbeatIntervalId);
          heartbeatIntervalId = null;
        }
        if (healthCheckIntervalId) {
          clearInterval(healthCheckIntervalId);
          healthCheckIntervalId = null;
        }

        // Attempt reconnection if not intentionally closing
        if (!isIntentionallyClosing && reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
          reconnectAttempts++;
          // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts - 1), 30000);
          console.log(`Reconnecting WebSocket in ${delay}ms (attempt ${reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeoutId = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
          console.error("Max reconnection attempts reached. Please refresh the page.");
        }
      };
    };

    // Initial connection
    connectWebSocket();

    // Cleanup on unmount
    return () => {
      isIntentionallyClosing = true;
      
      if (reconnectTimeoutId) {
        clearTimeout(reconnectTimeoutId);
      }
      if (heartbeatIntervalId) {
        clearInterval(heartbeatIntervalId);
      }
      if (healthCheckIntervalId) {
        clearInterval(healthCheckIntervalId);
      }
      if (websocket) {
        websocket.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]); // Only depend on playerId - use refs for callbacks to avoid reconnection

  const isEliminated = playerRole === PlayerRole.SPACESHIP && (playerDamage ?? 0) >= MAX_ALLOWED_SPACESHIP_DAMAGE;

  // Function to fetch landing site investment matrix
  const fetchLandingSiteMatrix = useCallback(async () => {
    if (!playerGameName || playerRole !== PlayerRole.AGENCY) return;
    
    setIsLoadingLandingSiteMatrix(true);
    try {
      // Fetch all players in the game
      const res = await authenticatedPlayerFetch(`/players/by-game-name?game_name=${encodeURIComponent(playerGameName)}`);
      if (!res.ok) {
        throw new Error("Failed to fetch players");
      }
      const players = await res.json();
      
      // Get all unique teams and their agency players
      const teamsMap = new Map<string, string>(); // team_name -> agency player_id
      const teams: string[] = [];
      
      Array.isArray(players) && players.forEach((p: any) => {
        if (p.role === PlayerRole.AGENCY && p.team_name) {
          if (!teamsMap.has(p.team_name)) {
            teamsMap.set(p.team_name, p.player_id);
            teams.push(p.team_name);
          }
        }
      });
      
      // Sort teams alphabetically for consistent display
      teams.sort();
      
      // Initialize investment matrix
      const investments: Record<string, Record<string, number>> = {};
      const totals: Record<string, number> = {};
      
      // Initialize all cells to 0
      teams.forEach((investingTeam) => {
        investments[investingTeam] = {};
        teams.forEach((targetTeam) => {
          investments[investingTeam][targetTeam] = 0;
        });
        totals[investingTeam] = 0;
      });
      
      // Fetch landing site investments for each team
      await Promise.all(
        teams.map(async (targetTeam) => {
          const targetPlayerId = teamsMap.get(targetTeam);
          if (!targetPlayerId) return;
          
          try {
            const pointsRes = await authenticatedPlayerFetch(
              `/players/landing-site-investments?player_id=${encodeURIComponent(targetPlayerId)}`
            );
            if (pointsRes.ok) {
              const pointsData = await pointsRes.json();
              if (Array.isArray(pointsData)) {
                pointsData.forEach((inv: any) => {
                  const investingTeam = inv.team_name;
                  const count = inv.count || 0;
                  
                  if (teams.includes(investingTeam) && teams.includes(targetTeam)) {
                    investments[investingTeam][targetTeam] = count;
                    totals[targetTeam] = (totals[targetTeam] || 0) + count;
                  }
                });
              }
            }
          } catch (err) {
            console.error(`Failed to fetch landing site investments for ${targetTeam}:`, err);
          }
        })
      );
      
      setLandingSiteMatrix({ teams, investments, totals });
    } catch (error) {
      console.error("Failed to fetch landing site matrix:", error);
      setLandingSiteMatrix(null);
    } finally {
      setIsLoadingLandingSiteMatrix(false);
    }
  }, [playerGameName, playerRole]);

  // Fetch landing site matrix when game name is available and player is agency
  useEffect(() => {
    if (playerGameName && playerRole === PlayerRole.AGENCY) {
      fetchLandingSiteMatrix();
    }
    // Update ref when function changes
    fetchLandingSiteMatrixRef.current = fetchLandingSiteMatrix;
  }, [playerGameName, playerRole, fetchLandingSiteMatrix]);

  return (
    <div style={{ padding: "1rem", backgroundColor: "#f8f9fa", minHeight: "100vh", display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* Error Toast */}
      <ErrorToast 
        message="An error occured, please refresh the page"
        isVisible={showErrorToast}
      />
      
      {/* Header */}
      <DashboardHeader 
        title={`Welcome, ${playerTeamName} ${playerRole}!`} 
        onLogout={onLogout}
        gameStatus={gameStatus}
        onRefresh={() => {
          localStorage.removeItem(`lost-in-space-player-data-${playerId}`);
          fetchDashboardData(true); // Force refresh
        }} 
        timerSeconds={timerSeconds}
        timerEnabled={timerEnabled}
        currentTurn={currentTurn}
        maxTurns={maxTurns}
        isLoadingTurn={isLoadingTurn}
      />

      {/* Cards Container - Side by Side */}
      {(() => {
        const infoCardItems = [
          ...(playerRole === PlayerRole.AGENCY ? [
            {
              label: `${playerTeamName}'s Spaceship Current Location`,
              value: spaceshipLocation || <em style={{ color: "#6c757d" }}>No location set</em>
            }
          ] : []),
        ];
        const hasActions = playerRole === PlayerRole.AGENCY;
        const shouldShowInfoCard = infoCardItems.length > 0 || hasActions;
        
        return (
          <div key="cards-container" style={{
            display: "flex",
            gap: "1rem",
            marginBottom: "1rem",
            alignItems: "stretch",
            justifyContent: shouldShowInfoCard ? "flex-start" : "center"
          }}>
        {/* Player Information Card */}
        {shouldShowInfoCard && (
          <div style={{ flex: "1 1 50%", display: "flex" }}>
            <InfoCard
              title={`Game ${playerGameName}`}
              items={infoCardItems}
              actions={
                hasActions ? (
              <div style={{ marginTop: "0.5rem", paddingTop: "0.5rem" }}>
                <h3 style={{ margin: "0 0 0.5rem 0", color: "#212529", fontSize: "1.1rem", fontWeight: "600" }}>
                  Landing Site Points
                </h3>
                {isLoadingLandingSiteMatrix ? (
                  <div style={{ padding: "1rem", textAlign: "center", color: "#6c757d", fontSize: "0.85rem" }}>
                    Loading landing site investments...
                  </div>
                ) : landingSiteMatrix && landingSiteMatrix.teams.length > 0 ? (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.75rem" }}>
                      <thead>
                        <tr>
                          <th style={{ 
                            padding: "0.35rem 0.5rem", 
                            textAlign: "left", 
                            border: "1px solid #dee2e6",
                            backgroundColor: "#f8f9fa",
                            fontWeight: "600",
                            color: "#212529",
                            position: "sticky",
                            left: 0,
                            zIndex: 1
                          }}>
                            Investing Team
                          </th>
                          {landingSiteMatrix.teams.map((team) => {
                            const teamTotal = landingSiteMatrix.totals[team] || 0;
                            return (
                            <th
                              key={team}
                              style={{
                                padding: "0.35rem 0.5rem",
                                textAlign: "center",
                                border: "1px solid #dee2e6",
                                backgroundColor: "#f8f9fa",
                                fontWeight: "600",
                                color: "#212529",
                                minWidth: "120px",
                                fontSize: "0.7rem"
                              }}
                            >
                              <div style={{ fontSize: "0.75rem", color: "#212529", fontWeight: "400", lineHeight: "1.2" }}>
                                <div style={{ fontSize: "0.9rem", fontWeight: "700", marginBottom: "0.25rem" }}>{team}</div>
                                <div style={{ borderTop: "1px solid #dee2e6", margin: "0.25rem 0", paddingTop: "0.25rem" }}></div>
                                <div>total points: <strong style={{ color: "#6f42c1", fontWeight: "700" }}>{teamTotal}</strong></div>
                              </div>
                            </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {landingSiteMatrix.teams.map((investingTeam) => (
                          <tr key={investingTeam}>
                            <td
                              style={{
                                padding: "0.35rem 0.5rem",
                                border: "1px solid #dee2e6",
                                backgroundColor: investingTeam === playerTeamName ? "#f0f0ff" : "white",
                                fontWeight: "700",
                                fontSize: "0.9rem",
                                position: "sticky",
                                left: 0,
                                zIndex: 1
                              }}
                            >
                              {investingTeam}
                              {investingTeam === playerTeamName && (
                                <span style={{ marginLeft: "0.5rem", color: "#6f42c1", fontSize: "0.75rem" }}>
                                  (You)
                                </span>
                              )}
                            </td>
                            {landingSiteMatrix.teams.map((targetTeam) => (
                              <td
                                key={targetTeam}
                                style={{
                                  padding: "0.35rem 0.5rem",
                                  textAlign: "center",
                                  border: "1px solid #dee2e6",
                                  backgroundColor: targetTeam === playerTeamName ? "#f0f0ff" : "white",
                                  color: landingSiteMatrix.investments[investingTeam]?.[targetTeam] > 0 ? "#6f42c1" : "#6c757d",
                                  fontWeight: landingSiteMatrix.investments[investingTeam]?.[targetTeam] > 0 ? "600" : "400"
                                }}
                              >
                                {landingSiteMatrix.investments[investingTeam]?.[targetTeam] || 0}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div style={{ padding: "1rem", textAlign: "center", color: "#6c757d" }}>
                    No landing site data available
                  </div>
                )}
              </div>
            ) : undefined
              }
            />
          </div>
        )}
        
        {/* Send Message component - always visible */}
        <div style={{ flex: "1 1 50%", maxWidth: "50%", display: "flex" }}>
          <SendMessage
            gameName={playerGameName}
            userRole={playerRole}
            loginCode={playerLoginCode}
            userTeamName={playerTeamName}
            gameStatus={gameStatus}
            playerId={playerId}
            currentTurn={currentTurn}
            charsSentThisTurn={charsSentThisTurn}
            extraDisabled={gameStatus?.toUpperCase() !== "ACTIVE" || isEliminated || (timerEnabled && (isPaused || (timerSeconds ?? 0) === 0))}
            extraDisabledMessage={isEliminated ? "Player eliminated due to maximum spaceship damage." : undefined}
            timerEnabled={timerEnabled}
            initialSelectedPlayerId={replyTargetPlayerId || undefined}
            commLevel={playerCommLevel}
          />
        </div>
          </div>
        );
      })()}

      {/* Player Actions component with filter buttons */}
      <div>
        <PlayerActions 
          playerId={playerId}
          playerRole={playerRole} 
          gameStatus={gameStatus}
          actionCount={actionCount}
          spaceshipDamage={playerDamage}
          spaceshipShieldsOn={playerRole === PlayerRole.SPACESHIP ? playerShieldsOn : undefined}
          teamName={playerTeamName}
          gameName={playerGameName}
          commLevel={playerCommLevel}
          scanLevel={playerScanLevel}
          totalLandingSitePoints={playerRole === PlayerRole.AGENCY ? totalLandingSiteInvestments : undefined}
          currentTurn={currentTurn}
          timerEnabled={timerEnabled}
          limitActions={limitActions}
          currentLocation={playerRole === PlayerRole.SPACESHIP ? playerCurrentLocation : spaceshipLocation}
          rightButtons={[
            {
              label: "Messages",
              onClick: () => {
                const willShowMessages = activeView !== "viewMessages";
                setActiveView(willShowMessages ? "viewMessages" : null);
                // Clear notification when opening messages view
                if (willShowMessages) {
                  setHasUnreadMessages(false);
                }
              },
              backgroundColor: "#28a745",
              showNotification: hasUnreadMessages && activeView !== "viewMessages"
            },
            {
              label: "Actions",
              onClick: () => {
                const willShowActions = activeView !== "viewActions";
                setActiveView(willShowActions ? "viewActions" : null);
                // Clear notification when opening actions view
                if (willShowActions) {
                  setHasUnreadActions(false);
                }
              },
              backgroundColor: "#17a2b8",
              showNotification: hasUnreadActions && activeView !== "viewActions"
            }
          ]}
        />
      </div>


      {/* View Messages component - conditionally rendered */}
      {activeView === "viewMessages" && (
        <div>
          <ViewMessages
            teamName={playerTeamName}
            playerRole={playerRole}
            playerId={playerId}
            websocket={ws}
            gameStatus={gameStatus}
            isEliminated={isEliminated}
            timerEnabled={timerEnabled}
            isPaused={isPaused}
            timerSeconds={timerSeconds}
            onReply={(targetPlayerId) => {
              // Set reply target and open send message view
              setReplyTargetPlayerId(targetPlayerId);
              setActiveView("sendMessage");
            }}
          />
        </div>
      )}

      {/* View Actions component - conditionally rendered */}
      {activeView === "viewActions" && (
        <div>
          <ViewActions
            playerId={playerId}
            playerRole={playerRole}
          />
        </div>
      )}

      {/* Landing Site Status Modal */}
      <ActionModal
        isOpen={isLandingSiteModalOpen}
        onClose={() => setIsLandingSiteModalOpen(false)}
        title="Landing Site Points"
      >
        <LandingSiteStatusContent
          playerId={playerId}
          investments={landingSiteInvestments}
          isLoading={isLoadingInvestments}
          onLoadInvestments={setLandingSiteInvestments}
          onSetLoading={setIsLoadingInvestments}
        />
      </ActionModal>

    </div>
  );
}

